// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QtyField, { QtyFieldStyles } from "@tps/App/Components/Qty/QtyField";
import deleteCartLines from "@tps/App/Handlers/Cart/DeleteCartLines";
import xLoadingOverlay from "@tps/App/Handlers/XNG/LoadingOverlay";
import { removeShippingInformation } from "@tps/App/Services/CartService";
import dataTableStyles from "@tps/App/SharedStyles/MyAccountPages/List/DataTableStyles";
import { CustomWarehouseDto } from "@tps/XDL/types";
import { color, xScreen } from "@tps/XDL/Utilities";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen?: boolean;
    setIsOpen: (isOpen: boolean) => void;
    productContext: any;
}

const mapStateToProps = (state: ApplicationState) => {
    const currentCartState = getCurrentCartState(state);
    const isCartLoading = currentCartState.isLoading;

    return {
        isAuthenticated: getSession(state).isAuthenticated,
        shouldLoadCart: !currentCartState.value && !isCartLoading,
        cartLines: currentCartState.value?.cartLines || [],
        isCartPage: getCurrentPage(state).type === "CartPage",
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    loadCurrentCart,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    deleteCartLines: makeHandlerChainAwaitable(deleteCartLines),
    xLoadingOverlay,
    loadRealTimePricing,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DistributeOrderModalStyles {
    message?: TypographyPresentationProps;
    headWrapper?: InjectableCss;
    productName?: TypographyPresentationProps;
    dataTableRow?: DataTableRowProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeader?: DataTableHeaderPresentationProps;
    dataTableCell?: DataTableCellProps;
    bottomSection?: InjectableCss;
    totalWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    tableWrapper?: InjectableCss;
    qtyFieldStyles?: QtyFieldStyles;
    modal?: ModalPresentationProps;
    tableHeader?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsText?: TypographyPresentationProps;
}

export const distributeOrderModalStyles: DistributeOrderModalStyles = {
    headWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productName: {
        variant: "h5",
        css: css`
            margin-bottom: 8px;
        `,
    },
    message: {
        size: 14,
        lineHeight: "22px",
    },
    bottomSection: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 10px;
        `,
    },
    totalWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 7px;
        `,
    },
    label: {
        size: 14,
        lineHeight: "22px",
    },
    value: {
        size: 18,
        lineHeight: "20px",
        weight: "bold",
    },
    tableWrapper: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    dataTableHead: {
        css: css`
            background: transparent;
            border: 0;
        `,
    },
    dataTableHeader: {
        typographyProps: {
            css: css`
                background: transparent;
                color: ${color("text.main")};
                border: 0;
                line-height: 17px;
                font-size: 12px;
                white-space: normal;
                text-align: right;
            `,
        },
        css: css`
            background: transparent;
            color: ${color("text.main")};
            border: 0;
            max-width: 120px;
            min-width: 120px;
            line-height: 17px;
            font-size: 12px;
            &:not(:first-child) {
                max-width: 68px;
                min-width: 68px;
                white-space: normal;
                text-align: right;
            }
            &:nth-child(2) {
                text-align: center;
                > ${TypographyStyle} {
                    display: block;
                    margin: auto;
                    max-width: 68px;
                }
            }
            padding-bottom: 8px;
            padding-top: 0;
            &.selected-qty ${TypographyStyle} {
                text-align: center;
                ${xScreen("lte", "md")} {
                    text-align: right;
                }
            }
        `,
    },
    dataTableCell: {
        typographyProps: {
            css: css`
                border: 0;
                line-height: 17px;
                font-size: 12px;
                white-space: normal;
            `,
        },
        css: css`
            border: 0;
            max-width: 120px;
            min-width: 120px;
            font-size: 12px;
            line-height: 17px;
            &:not(:first-child) {
                max-width: 68px;
                min-width: 68px;
                white-space: normal;
                text-align: right;
            }
            padding-block: 10px;
            &:first-child ${TypographyStyle} {
                font-weight: bold;
            }
            &:nth-child(2) {
                max-width: 115px;
                min-width: 110px;
                padding-block: 5px;
                ${xScreen("lte", "md")} {
                    max-width: 74px;
                    min-width: 74px;
                }
            }
        `,
    },
    dataTableRow: {
        css: css`
            border: 1px solid ${color("common.ui4")};
        `,
    },
    qtyFieldStyles: {
        wrapper: {
            css: css`
                max-width: 115px;
                margin-inline: auto;
                ${xScreen("lte", "md")} {
                    max-width: 74px;
                }
            `,
        },
    },
    modal: {
        mergeCss: true,
        cssOverrides: {
            modalBody: css`
                ${xScreen("lt", "md")} {
                    max-width: 543px;
                }
                ${xScreen("lte", "sm")} {
                    max-width: 90%;
                }
            `,
        },
    },
    tableHeader: {
        css: css`
            display: block;
            max-width: 68px;
            margin-left: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 200px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsText: {
        ...dataTableStyles.noResultsText,
    },
};

const styles = distributeOrderModalStyles;

export interface AvailabilityTableRow {
    warehouseId: string;
    warehouseDescription: string;
    currentInventory: number;
    qtyOnCart: number;
    qtyToBeAdded: number;
}

const DistributeOrderModal = ({
    getRealTimeWarehouseInventory,
    isOpen,
    productContext: { product, productInfo },
    setIsOpen,
    loadCurrentCart,
    shouldLoadCart,
    cartLines,
    addCartLines,
    isCartPage,
    deleteCartLines,
    xLoadingOverlay,
    loadRealTimePricing,
}: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [availabilityTable, setAvailabilityTable] = useState<AvailabilityTableRow[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [deleteWarehouseId, setDeleteWarehouseId] = useState<string[]>([]);
    const toasterContext = useContext(ToasterContext);
    const [disabledAddToCartButton, setDisabledAddToCartButton] = useState(false);

    if (!product || !productInfo) {
        return null;
    }

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
        }
    }, [shouldLoadCart]);

    useEffect(() => {
        getAvailabilityByWarehouse();
    }, [cartLines]);

    useEffect(() => {
        // Check if button needs to be disabled or not
        if (availabilityTable.find(row => row.qtyToBeAdded > row.currentInventory)) {
            setDisabledAddToCartButton(true);
        } else {
            setDisabledAddToCartButton(false);
        }
    }, [availabilityTable]);

    const getProductQtyAddedToCart = (warehouseId: string, productId: string) => {
        const matchedCartLine = cartLines?.find(
            cartLine => cartLine.properties.warehouseId === warehouseId && cartLine.productId === productId,
        );
        return matchedCartLine?.qtyOrdered || 0;
    };

    const getProductAllQtyAddedToCart = (productId: string) => {
        let qty = 0;
        cartLines?.forEach(cartLine => {
            if (cartLine.productId === productId) {
                qty += cartLine.qtyOrdered || 0;
            }
        });
        return qty;
    };

    const buildWarehousesTable = (
        warehouses: CustomWarehouseDto[] | undefined,
        selectedWrehouse: AvailabilityTableRow,
    ) => {
        let qty = isCartPage ? productInfo.qtyOrdered : productInfo.qtyOrdered + selectedWrehouse.qtyOnCart;
        const selectedQty = productInfo.qtyOrdered;
        setAvailabilityTable([]);
        qty = qty - (selectedWrehouse?.qtyToBeAdded || 0);

        // now qty is the extra quantity that we need to distribute,

        const numOfQtyInCart = getProductAllQtyAddedToCart(productInfo.productId);

        warehouses?.forEach(warehouse => {
            const warehouseId = warehouse.properties.warehouseId;
            let qtyOnCart = getProductQtyAddedToCart(warehouseId, productInfo.productId);
            if (warehouse.qty <= 0) {
                // If warehouse has zero qty, we should be able to delete it when we distribute order.
                if (isCartPage && qtyOnCart !== 0) {
                    setDeleteWarehouseId(prev => [...prev, warehouseId]);
                }
                return;
            }

            // In cart, max value for Already in cart is warehouse qty
            if (isCartPage && qtyOnCart > warehouse.qty) {
                qtyOnCart = warehouse.qty;
            }
            let totalNewLineQtyOnCart = 0;
            if (warehouseId === product.properties.warehouseId && selectedWrehouse) {
                setAvailabilityTable(prevData => [...prevData, selectedWrehouse]);
                return;
            }
            if (qty > 0) {
                totalNewLineQtyOnCart = Math.min(warehouse.qty - qtyOnCart, qty) + qtyOnCart;
                qty -= Math.min(warehouse.qty - qtyOnCart, qty);
            } else {
                totalNewLineQtyOnCart = qtyOnCart;
            }
            // TPS-803 current inventory is either warehouse.qtyOnHand or selectedQty + numOfQtyInCart
            // TPS-876: qtyToBeAdded initially is 0
            // TPS-1041: currentInventory in cart page should not exceed value to total items in the cart
            const currentInventory = isCartPage
                ? Math.min(numOfQtyInCart, warehouse.qty)
                : Math.min(selectedQty + numOfQtyInCart, warehouse.qty);

            const line: AvailabilityTableRow = {
                warehouseId,
                warehouseDescription: warehouse.description,
                currentInventory,
                qtyOnCart,
                qtyToBeAdded: 0,
            };
            setAvailabilityTable(prevData => [...prevData, line]);
        });
    };

    const findSelectedWarehouse = (warehouses: CustomWarehouseDto[] | undefined) => {
        const warehouse = warehouses?.find(
            warehouse => warehouse.properties.warehouseId === product.properties.warehouseId,
        );
        const warehouseId = warehouse?.properties.warehouseId;

        if (!warehouse || !warehouseId) {
            return null;
        }

        const qtyOnCart = (warehouseId && getProductQtyAddedToCart(warehouseId, productInfo.productId)) || 0;

        const line: AvailabilityTableRow = {
            warehouseId,
            warehouseDescription: warehouse.description,
            currentInventory: warehouse.qty,
            qtyOnCart,
            // TPS-876
            qtyToBeAdded: 0,
        };
        buildWarehousesTable(warehouses as CustomWarehouseDto[], line);
    };

    const getAvailabilityByWarehouse = () => {
        setIsLoading(true);
        loadRealTimePricing({
            productPriceParameters: productInfo,
            onComplete() {
                getRealTimeWarehouseInventory({
                    productId: productInfo.productId,
                    unitOfMeasure: productInfo.unitOfMeasure,
                    onComplete: result => {
                        findSelectedWarehouse(result.warehouses as CustomWarehouseDto[]);
                        setErrorMessage(result.errorMessage);
                        setIsLoading(false);
                    },
                });
            },
        });
    };

    const qtyOrderedChangeHandler = (event: any, availabilityRow: AvailabilityTableRow) => {
        const newTable = availabilityTable.map(item => {
            if (item.warehouseId === availabilityRow.warehouseId) {
                return {
                    warehouseId: availabilityRow.warehouseId,
                    warehouseDescription: availabilityRow.warehouseDescription,
                    currentInventory: availabilityRow.currentInventory,
                    qtyOnCart: availabilityRow.qtyOnCart,
                    qtyToBeAdded: Number(event.target.value),
                };
            }
            return item;
        });
        setAvailabilityTable([...newTable]);
    };

    const handleSubmitClick = async () => {
        const productsInfos: any[] = [];
        const warehousesIdsForDelete: string[] = [];
        availabilityTable.forEach(row => {
            if (row.qtyToBeAdded !== 0 && row.qtyToBeAdded !== row.qtyOnCart) {
                productsInfos.push({
                    productId: productInfo.productId,
                    unitOfMeasure: productInfo.unitOfMeasure,
                    qtyOrdered: row.qtyToBeAdded - row.qtyOnCart, // send the change in qty
                    properties: { warehouseId: row.warehouseId },
                });
            }

            if (row.qtyToBeAdded === 0 && row.qtyOnCart > 0) {
                // push all cartlines that will be deleted to an array
                warehousesIdsForDelete.push(row.warehouseId);
            }
        });

        if (deleteWarehouseId.length > 0) {
            warehousesIdsForDelete.push(...deleteWarehouseId);
        }
        const cleanDeleteWarehouseIds = Array.from(new Set(warehousesIdsForDelete));

        const cartLinesIds: string[] = [];
        const cartLinesForDelete: CartLineModel[] = [];
        cleanDeleteWarehouseIds.forEach(warehouseId => {
            const cartLineForDelete = cartLines.find(
                cartLine => cartLine.properties.warehouseId === warehouseId && cartLine.productId === product.id,
            );
            const id = cartLineForDelete?.id;

            id && cartLinesIds.push(id);
            cartLineForDelete && cartLinesForDelete.push(cartLineForDelete);
        });

        xLoadingOverlay({ key: "add", id: "update_cart_quantity" });

        if (productsInfos && productsInfos.length > 0) {
            const cartLinesCollection = (await addCartLines({
                productInfos: productsInfos,
            })) as CartLineCollectionModel;

            const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                messageType: "success",
            });

            // remove shipping details from table, if needed
            if (cartLinesForDelete && cartLinesForDelete.length > 0) {
                const ids: string[] = [];
                cartLinesForDelete.forEach(line => {
                    if (
                        !cartLines?.some(
                            cartLine =>
                                cartLine.id !== line.id &&
                                cartLine.properties.warehouseId === line.properties.warehouseId,
                        )
                    ) {
                        // this means its the only cartLine in the warehouse, so we need to remove the warehouse shipping info
                        ids.push(line.properties.warehouseId);
                    }
                });
                // now call the api and send ids
                ids.length > 0 && removeShippingInformation(ids);
            }

            if (cartLinesIds && cartLinesIds.length > 0) {
                await deleteCartLines({
                    cartLinesIds,
                });
                // reset value after delete cartlines
                setDeleteWarehouseId([]);
            }

            loadCurrentCart({ shouldLoadFullCart: true });
            xLoadingOverlay({ key: "clear", id: "update_cart_quantity" });
            setIsOpen(false);
            return;
        }

        if (cartLinesIds && cartLinesIds.length > 0) {
            await deleteCartLines({
                cartLinesIds,
            });
            // reset value after delete cartlines
            setDeleteWarehouseId([]);
            loadCurrentCart({ shouldLoadFullCart: true });
        }
        xLoadingOverlay({ key: "clear", id: "update_cart_quantity" });
        setIsOpen(false);
    };

    const handleClearQtyClick = () => {
        const newTable = availabilityTable.map(item => {
            return {
                ...item,
                qtyToBeAdded: 0,
            };
        });
        setAvailabilityTable([...newTable]);
    };

    const availableWarehouses = useMemo(
        () => availabilityTable.filter(row => row.currentInventory && row.currentInventory >= 1),
        [availabilityTable],
    );

    const headLine = () => {
        if (isLoading) {
            return null;
        }
        return (
            <StyledWrapper {...styles.headWrapper}>
                {product.productTitle && <Typography {...styles.productName}>{product.productTitle}</Typography>}
                {availableWarehouses.length > 0 && (
                    <Typography {...styles.message}>
                        {siteMessage("Custom_DistributeOrderModal_NoEnoughInventory")}
                    </Typography>
                )}
            </StyledWrapper>
        );
    };

    return (
        <Modal
            {...styles.modal}
            sizeVariant="medium"
            headline={headLine()}
            isOpen={isOpen}
            handleClose={() => setIsOpen(false)}
        >
            {isLoading ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : (
                <>
                    {availableWarehouses.length > 0 ? (
                        <>
                            <StyledWrapper {...styles.tableWrapper}>
                                <DataTable>
                                    <DataTableHead {...styles.dataTableHead}>
                                        <DataTableHeader {...styles.dataTableHeader} />
                                        <DataTableHeader {...styles.dataTableHeader} className="selected-qty">
                                            {translate("Select Quantity")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeader}>
                                            <Typography {...styles.tableHeader}>
                                                {translate("Already In Cart")}
                                            </Typography>
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeader}>
                                            <Typography {...styles.tableHeader}>
                                                {translate("Current Inventory")}
                                            </Typography>
                                        </DataTableHeader>
                                    </DataTableHead>
                                    <DataTableBody>
                                        {availableWarehouses.map((availabilityRow, index) => {
                                            return (
                                                <DataTableRow {...styles.dataTableRow} key={`${index}`}>
                                                    <DataTableCell {...styles.dataTableCell}>
                                                        {availabilityRow.warehouseDescription}
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.dataTableCell}>
                                                        <QtyField
                                                            allowZero={true}
                                                            minValue={0}
                                                            maxValue={availabilityRow.currentInventory}
                                                            maxLength={
                                                                availabilityRow.currentInventory.toString().length
                                                            }
                                                            value={availabilityRow.qtyToBeAdded}
                                                            onChange={event =>
                                                                qtyOrderedChangeHandler(event, availabilityRow)
                                                            }
                                                            onFocus={e => e.target.select()}
                                                            extendedStyles={styles.qtyFieldStyles}
                                                            showLabel={false}
                                                        />
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.dataTableCell}>
                                                        {isCartPage &&
                                                        availabilityRow.warehouseId === product.properties.warehouseId
                                                            ? availabilityRow.currentInventory
                                                            : availabilityRow.qtyOnCart}
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.dataTableCell}>
                                                        {availabilityRow.currentInventory}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            );
                                        })}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                            <StyledWrapper {...styles.bottomSection}>
                                <Button aria-label={translate("clear quantity")} onClick={handleClearQtyClick}>
                                    {siteMessage("Custom_clear_quantity")}
                                </Button>
                                <StyledWrapper {...styles.totalWrapper}>
                                    <Typography {...styles.label}>{translate("Total:")}</Typography>
                                    <Typography {...styles.value}>
                                        {availableWarehouses
                                            .map(row => row.qtyToBeAdded)
                                            .reduce(
                                                (acc, qtyToBeAdded) => acc + parseInt(qtyToBeAdded.toString(), 10),
                                                0,
                                            )}
                                    </Typography>
                                </StyledWrapper>
                                <Button
                                    disabled={disabledAddToCartButton}
                                    aria-label={translate("Add Quantity to Cart")}
                                    onClick={handleSubmitClick}
                                >
                                    {translate("Add Quantity to Cart")}
                                </Button>
                            </StyledWrapper>
                        </>
                    ) : (
                        <Typography {...styles.noResultsText}>
                            {isCartPage
                                ? siteMessage("Custom_No_Warehouse_Inventory_CartPage")
                                : siteMessage("Custom_No_Warehouse_Inventory_PDP")}
                        </Typography>
                    )}
                </>
            )}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DistributeOrderModal);
